import { useEffect, useState } from 'react';
import { TarotPreview } from "./previews/TarotPreview"
import { FantasyPreview } from "./previews/FantasyPreview"
import { FlyerPreview } from "./previews/FlyerPreview"
import { UFOPreview } from "./previews/UFOPreview"
import { CardPreviewProps, GeneratedCardData, MTGCardData, TarotCardData } from "@/types/card"
import FineControls from "./FineControls"
import { Button } from "@/components/ui/button";
import { CARD_CATEGORIES } from "@/lib/utils/constants/cards";

// Add this array of loading messages at the top of the file, outside the component
const loadingMessages = [
  "Gathering magical essence...",
  "Channeling creative energy...",
  "Consulting the ancient tomes...",
  "Weaving card elements...",
  "Infusing with power...",
  "Materializing your creation...",
  "Adding finishing touches...",
];

const getThemeType = (data: any): string => {
  // If theme string is provided, use it
  if (data.theme) {
    return data.theme.toLowerCase();
  }
  
  // If only themeId is provided, look up the type
  if (data.themeId) {
    // Find the category key by themeId
    const categoryEntry = Object.entries(CARD_CATEGORIES).find(
      ([_, category]) => category.themeId === data.themeId
    );
    return categoryEntry ? categoryEntry[0].toLowerCase() : '';
  }
  
  return '';
};

export function CardPreview({ data, cardData: initialCardData, isLoading, onBack }: CardPreviewProps) {
  const [cardData, setCardData] = useState<GeneratedCardData | null>(initialCardData);
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);

  // Update cardData when initialCardData changes
  useEffect(() => {
    setCardData(initialCardData);
  }, [initialCardData]);

  // Add this effect to handle loading message rotation
  useEffect(() => {
    let intervalId: NodeJS.Timeout;
    
    if (isLoading) {
      intervalId = setInterval(() => {
        setLoadingMessageIndex((prev) => (prev + 1) % loadingMessages.length);
      }, 2000); // Change message every 2 seconds
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
      setLoadingMessageIndex(0); // Reset to first message when loading stops
    };
  }, [isLoading]);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center p-8 min-h-[300px] bg-black/5 rounded-lg">
        <div className="animate-spin mb-6">
          <div className="w-10 h-10 border-4 border-zinc-800 border-t-zinc-400 rounded-full"/>
        </div>
        <div className="h-12 flex items-center justify-center"> {/* Fixed height container */}
          <span 
            key={loadingMessageIndex}
            className="text-lg text-zinc-700 animate-fadeIn absolute"
            style={{
              animation: "fadeInOut 2s ease-in-out",
            }}
          >
            {loadingMessages[loadingMessageIndex]}
          </span>
        </div>
      </div>
    );
  }

  if (!cardData) {
    return (
      <div className="flex flex-col items-center justify-center p-8 min-h-[300px] bg-black/5 rounded-lg">
        <div>No card data available</div>
        <Button variant="outline" onClick={onBack} className="mt-4">
          Back to Editor
        </Button>
      </div>
    );
  }

  // Add at the beginning of the CardPreview component
  console.log('CardPreview received props:', { data, initialCardData, isLoading });

  // Transform cardData to the correct type
  const transformCardData = (data: GeneratedCardData): MTGCardData | TarotCardData => {
    console.log('CardPreview transforming data:', data);
    
    // Parse metadata once if needed
    const metadata = typeof data.metadata === 'string' ? 
      JSON.parse(data.metadata) : data.metadata;

    // Return a clean structure with all needed fields
    return {
      ...data,
      metadata: {
        ...metadata,
        // Ensure required fields exist
        type: metadata.type || metadata.cardType || 'Unknown',
        manaCost: metadata.manaCost || '',
        flavorText: metadata.flavorText || '',
        rarity: metadata.rarity || 'common',
        language: metadata.language || 'en',
        playerName: metadata.playerName || 'Anonymous'
      }
    } as MTGCardData | TarotCardData;
  };

  const transformedData = transformCardData(cardData);
  // console.log('Transformed card data:', transformedData);

  const handleUpdate = (field: string, value: string) => {
    // console.log(`Updating ${field} with value:`, value);
    
    setCardData(prev => {
      if (!prev) return prev;
      
      const updated = {
        ...prev,
        // If the field is 'imageUrl', update the imageUrl property
        ...(field === 'imageUrl' ? { imageUrl: value } : { [field]: value })
      };
      console.log('Updated card data:', updated);
      return updated as GeneratedCardData;
    });
  };

  // Add this CSS to your global styles or as a style tag in your component
  const styles = `
    @keyframes fadeInOut {
      0% {
        opacity: 0;
        transform: translateY(10px);
      }
      20%, 80% {
        opacity: 1;
        transform: translateY(0);
      }
      100% {
        opacity: 0;
        transform: translateY(-10px);
      }
    }
  `;

  const themeId = data.themeId ?? 0;
  if (!data.themeId) {
    return null;
  }

  return (
    <>
      <style>{styles}</style>
      <div className="rounded-xl border border-zinc-800 p-6">
        <div className="flex flex-col lg:flex-row gap-8">
          <div className="w-full lg:flex-1 flex items-start">
            {(() => {
              const themeType = getThemeType(data);
              switch (themeType) {
                case 'fantasy':
                  return <FantasyPreview data={transformedData as MTGCardData} onBack={onBack} />;
                case 'tarot':
                  return <TarotPreview data={transformedData as TarotCardData} onBack={onBack} />;
                case 'flyers':
                  return <FlyerPreview data={cardData} onBack={onBack} />;
                case 'ufo':
                  return <UFOPreview data={cardData} onBack={onBack} />;
                default:
                  return <div>Unsupported card type</div>;
              }
            })()}
          </div>
          <div className="w-full lg:flex-1">
            <FineControls 
              cardData={{
                cardId: cardData.id || 0,
                theme: getThemeType(data),
                themeId: themeId,
                fields: data.fields,
                language: data.language,
                playerName: data.playerName,
                userId: (data as any).userId || '0',
                imageUrl: (data as any).imageUrl || ''
              }}
              onUpdate={handleUpdate}
            />
          </div>
        </div>
      </div>
    </>
  );
}